
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GlassCard } from "@/components/ui/glass-card";
import { GameCard } from "@/components/coach/game-card";
import { Game } from "@/types";
import { getGames, getGameVotes } from "@/services/mockData";

export const GamesList = () => {
  const [activeGames, setActiveGames] = useState<Game[]>([]);
  const [voteCounts, setVoteCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const games = await getGames();
        setActiveGames(games);
        
        const votesData: Record<string, number> = {};
        
        await Promise.all(
          games.map(async (game) => {
            const votes = await getGameVotes(game.id);
            const uniqueSessions = new Set(votes.map(vote => vote.voter_session_id));
            votesData[game.id] = uniqueSessions.size;
          })
        );
        
        setVoteCounts(votesData);
      } catch (error) {
        console.error("Error fetching games:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.4 }
    }
  };
  
  return (
    <section>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Games</h2>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <GlassCard key={i} className="animate-pulse">
              <div className="h-6 w-36 bg-primary/20 rounded mb-4"></div>
              <div className="h-8 w-48 bg-primary/20 rounded mb-4"></div>
              <div className="h-4 w-24 bg-primary/10 rounded mb-8"></div>
              <div className="h-10 w-full bg-primary/20 rounded"></div>
            </GlassCard>
          ))}
        </div>
      ) : activeGames.length > 0 ? (
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {activeGames.map(game => (
            <motion.div key={game.id} variants={itemVariants}>
              <GameCard 
                game={game} 
                voteCounts={voteCounts[game.id] || 0}
              />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <GlassCard className="text-center p-8">
          <h3 className="text-xl font-semibold mb-2">No matches yet</h3>
          <p className="text-muted-foreground">
            Create a new match to get started.
          </p>
        </GlassCard>
      )}
    </section>
  );
};
